import React, { useEffect, useState } from 'react';
import { ErrorText, FieldGroup, Row, Stack, Text, TimePicker } from 'library/ui';
import { FormFieldProps } from '../../FormTypes';
import { timeWithDate } from 'parent-portal/forms/time-operations';
import moment from 'moment-timezone';
import { JobTimes } from 'parent-portal/forms/JobRequestTypes';
import SlotsPicker from './SlotsPicker';

export default function JobTimePicker({
    jobTimes,
    startDate,
    allowPastMidnight = true,
    title,
    updateAllSlots,
    essentialOnly = false,
}: {
    jobTimes: FormFieldProps<JobTimes>;
    startDate?: moment.Moment | null;
    allowPastMidnight?: boolean;
    title?: string;
    updateAllSlots?: (slots: number) => void;
    essentialOnly?: boolean;
}) {
    const { start, end, timezone } = jobTimes.value;

    const [durationIsValid, setDurationValid] = useState(true);
    const [startTimeIsValid, setStartTimeValid] = useState(true);
    const [endTimeIsValid, setEndTimeValid] = useState(true);

    const [startIsNull, setStartIsNull] = useState(false);
    const [endIsNull, setEndIsNull] = useState(false);

    useEffect(() => updateValidStates(jobTimes.value), [jobTimes, startIsNull, endIsNull]);
    useEffect(
        () => updateFieldValidity(durationIsValid, startTimeIsValid, endTimeIsValid),
        [durationIsValid, startTimeIsValid, endTimeIsValid],
    );

    function updateFieldValidity(durationIsValid: boolean, startTimeIsValid: boolean, endTimeIsValid: boolean) {
        jobTimes.setValid(durationIsValid && startTimeIsValid && endTimeIsValid);
    }

    function updateValidStates({ start, end }: JobTimes) {
        const now = moment.utc();

        const startIsDefined = !startIsNull && start !== null;
        const startIsNotInThePast = startIsDefined && timeWithDate(start!, startDate ?? moment()).isAfter(now);
        setStartTimeValid(startIsDefined && startIsNotInThePast);

        setEndTimeValid(!endIsNull && !!end && (!start || endTimeAllowPastMidnight(start, end)));

        const durationIsNotDefined = !start || !end;
        const newDurationIsValid = durationIsNotDefined || durationMeetsMinimum(start!, end!);
        setDurationValid(newDurationIsValid);
    }

    function durationMeetsMinimum(start: moment.Moment, end: moment.Moment) {
        const dateForEnd = moment().set({
            hour: end.get('hour'),
            minute: end.get('minute'),
        });
        const dateForStart = moment().set({
            hour: start.get('hour'),
            minute: start.get('minute'),
        });

        return dateForEnd.diff(dateForStart, 'minutes') + 1 > 30;
    }

    function onStartTimeChanged(time: moment.Moment | null) {
        if (!time || !time.isValid()) {
            setStartIsNull(true);
            return;
        }
        setStartIsNull(false);

        const dateContext = timezone ? moment.tz(startDate ?? moment(), timezone) : moment(startDate ?? moment());
        const dateTime = dateContext.clone().set({
            hour: time.get('hour'),
            minute: time.get('minute'),
            second: 0,
            millisecond: 0,
        });

        jobTimes.setValue({
            ...jobTimes.value,
            start: dateTime,
        });
    }

    function onEndTimeChanged(time: moment.Moment | null) {
        if (!time || !time.isValid()) {
            setEndIsNull(true);
            return;
        }
        setEndIsNull(false);

        const dateContext = timezone ? moment.tz(startDate ?? moment(), timezone) : moment(startDate ?? moment());
        const dateTime = dateContext.clone().set({
            hour: time.get('hour'),
            minute: time.get('minute'),
            second: 0,
            millisecond: 0,
        });

        jobTimes.setValue({
            ...jobTimes.value,
            end: dateTime,
        });
    }

    function endTimeAllowPastMidnight(start: moment.Moment, end: moment.Moment) {
        if (!allowPastMidnight) {
            const dateForEnd = moment().set({
                hour: end.get('hour'),
                minute: end.get('minute'),
            });
            const dateForStart = moment().set({
                hour: start.get('hour'),
                minute: start.get('minute'),
            });

            return !dateForEnd.isBefore(dateForStart);
        }
        return true;
    }

    function toTimeString(time: moment.Moment | null) {
        if (!time) {
            return '';
        }
        return time.format('HH:mm');
    }

    function fromTimeString(value: string) {
        return value ? moment(value, 'HH:mm:ss') : null;
    }

    return (
        <Stack gap="sm">
            {title && <Text weight="semibold">{title}</Text>}
            <Row gap="md" wrap align="start">
                <FieldGroup label="Start">
                    <TimePicker
                        value={toTimeString(start)}
                        emptyDefault="09:00"
                        onChange={(value) => onStartTimeChanged(fromTimeString(value))}
                    />
                    {!startTimeIsValid && !!start && <ErrorText>Start time cannot be in the past</ErrorText>}
                    {!start && !!end && <ErrorText>Start time is required</ErrorText>}
                </FieldGroup>
                <FieldGroup label="End">
                    <TimePicker
                        value={toTimeString(end)}
                        emptyDefault="17:00"
                        onChange={(value) => onEndTimeChanged(fromTimeString(value))}
                    />
                    {!endTimeIsValid && !!end && <ErrorText>End time must be after start time</ErrorText>}
                    {!durationIsValid && <ErrorText>Duration must be at least 30 minutes</ErrorText>}
                </FieldGroup>
                {updateAllSlots && (
                    <SlotsPicker
                        slotsAvailable={essentialOnly ? 1 : jobTimes.value.slots}
                        setSlotsAvailable={updateAllSlots}
                        label="Number of workers"
                        disabled={essentialOnly}
                    />
                )}
            </Row>
        </Stack>
    );
}
